// src/components/OrderEntry/utils/freight.js
// =============================================================================
// FREIGHT / FOB UTILITIES
// =============================================================================
// Extracted from OrderEntry.jsx BLOCK OE.7B
// Rules:
//  - UPS: ZZZ-ZZZ or ZZZ ZZZ or ZZZZZZ (6 alphanumeric after stripping)
//  - FedEx: ######### (9 digits after stripping)
// =============================================================================

/**
 * Normalize FOB candidate string
 * Strips spaces, dashes, and non-alphanumeric characters
 *
 * @param {string} raw - Raw FOB string
 * @returns {object} Normalized FOB data
 *   - raw: original string
 *   - compact: uppercase alphanumeric only
 *   - digits: digits only
 */
export function normalizeFobCandidate(raw) {
  const s = String(raw ?? '').trim();
  if (!s) {
    return { raw: '', compact: '', digits: '' };
  }

  const compact = s
    .replace(/[\s-]/g, '')
    .replace(/[^A-Za-z0-9]/g, '')
    .toUpperCase();
  const digits = compact.replace(/\D/g, '');
  return { raw: s, compact, digits };
}

/**
 * Infer carrier from FOB string
 * Rules:
 *  - UPS: 6 alphanumeric after stripping (ZZZ-ZZZ or ZZZ ZZZ or ZZZZZZ)
 *  - FedEx: 9 digits after stripping
 *
 * @param {string} fob - FOB string
 * @returns {string|null} 'FDX', 'UPS', or null
 */
export function inferCarrierFromFob(fob) {
  const { compact } = normalizeFobCandidate(fob);
  if (!compact) {
    return null;
  }

  if (/^\d{9}$/.test(compact)) {
    return 'FDX';
  }

  if (/^[A-Z0-9]{6}$/.test(compact)) {
    return 'UPS';
  }

  return null;
}

// =============================================================================
// BLOCK OE.FRT.VALIDATE.0 — Strict account validators (UPS / FDX)
// ----------------------------------------------------------------------------
// Matches backend rules in server/routes/.../most-recent-fob.js
// - UPS: 6 alphanumeric
// - FDX: 9 digits
// =============================================================================

export function isUpsAccount(acct) {
  const { compact } = normalizeFobCandidate(acct);
  return /^[A-Z0-9]{6}$/.test(compact);
}

export function isFedexAccount(acct) {
  const { compact } = normalizeFobCandidate(acct);
  return /^\d{9}$/.test(compact);
}

/**
 * Validate freight account for a specific carrier
 * - For UPS: must be 6 alphanumeric
 * - For FDX/FEDEX: must be 9 digits
 * - For other carriers: returns true (no strict rule)
 */
export function isValidFreightAccountForCarrier({ carrier, accountNo }) {
  const c = String(carrier ?? '')
    .trim()
    .toUpperCase();
  const a = String(accountNo ?? '').trim();

  // blank is invalid when strict carrier
  if (!a) {
    return !(c === 'UPS' || c === 'FDX' || c === 'FEDEX');
  }

  if (c === 'UPS') {
    return isUpsAccount(a);
  }

  if (c === 'FDX' || c === 'FEDEX') {
    return isFedexAccount(a);
  }

  return true;
}
